import {List, Map} from 'immutable'
import PropTypes from 'prop-types'
import {useEffect, useRef} from 'react'
import {useSelector} from 'react-redux'

import AudioPlayerBar from '../../../../../shared_components/audio_player/AudioPlayerBar'
import {AudioPlayerProvider, useAudioPlayer} from '../../../../../shared_components/audio_player/AudioPlayerContext'
import Closer from '../../../../../shared_components/core/closer/Closer'
import FileView from '../../../../../shared_components/forms/v2/form_helpers/FileView'
import LifeStoryItemHeader from './LifeStoryItemHeader'
import Modals from '../../../../../shared_components/core/modals/Modals'
import {ModalHeaderWithChildren} from '../../../../../shared_components/core/modals/ModalHeaders'
import {RECORDING_FINISHED_FRACTION, trackLifeStoryEvent} from './lifeStoryEventLogger'
import {attachmentsWithPdfFirst} from './lifeStoryTools'
import {categoryByItem} from '../../../../../lib/plan_data/category'
import {currentActor} from '../../../../../lib/userTools'
import {itemResponseGroupByGroupId} from '../../../../../lib/plan_data/itemResponsesHelper'

import './lifeStoryItemModal.scss'

/**
 * Logs a deputy finishing a recording played from the preview modal, as the
 * story screen does (TK-2993): once per recording, the first time
 * RECORDING_FINISHED_FRACTION of it has been heard. Renders nothing; it lives
 * inside the modal's AudioPlayerProvider to read the player.
 */
const DeputyRecordingFinishLogger = ({userConfig}) => {
  const {currentTrack, duration, played} = useAudioPlayer()
  const finishedRecordingsRef = useRef(new Set())

  useEffect(() => {
    if (!currentTrack || !duration || finishedRecordingsRef.current.has(currentTrack.id)) return

    if (played / duration >= RECORDING_FINISHED_FRACTION) {
      finishedRecordingsRef.current.add(currentTrack.id)
      trackLifeStoryEvent({eventName: 'finish_my_story_audio', userConfig, viewerIsOwner: false})
    }
  }, [currentTrack, duration, played])

  return null
}

DeputyRecordingFinishLogger.propTypes = {
  userConfig: PropTypes.instanceOf(Map)
}

const LifeStoryItemModal = ({context, ethicalWill, itemResponse, groupId, exit, viewerIsOwner = true, ownerFirstName, everplanId = null, userConfig}) => {
  const fileResponse = itemResponse.get('responses').find(response => (
    response.get('group-id').includes(groupId) && response.get('element-type') === 'FILE'
  ))
  const files = fileResponse ? (fileResponse.getIn(['value', 'data']) || List()) : List()

  const itemName = itemResponse.get('name')
  const actor = currentActor({ownEverplan: viewerIsOwner, context})

  const categories = useSelector(state => state.categories)
  const itemCategory = categoryByItem({categories, item: itemResponse})
  const responseGroupKeyField = itemResponseGroupByGroupId({itemResponse, groupId}).get('key-field', List())
  const keyField = responseGroupKeyField.isEmpty() ? List([itemResponse.get('header')]) : responseGroupKeyField

  const modalCloser = () => <Closer closer={exit} />

  return (
    <div className='life-story-item-modal'>
      <AudioPlayerProvider>
        <Modals.CardModal
          ariaLabel={itemName}
          className='item forms-playground item-edit-modal'
          closerComponent={modalCloser}
          showModal={true}>
          <ModalHeaderWithChildren>
            <div className='item-heading'>
              <h2>{keyField}</h2>
              {itemCategory && <div className='item-category-name'>{itemCategory.get('display-name')}</div>}
            </div>
          </ModalHeaderWithChildren>
          <div className='item-body'>
            <LifeStoryItemHeader
              ethicalWill={ethicalWill}
              everplanId={everplanId}
              ownerFirstName={ownerFirstName}
              userConfig={userConfig}
              viewerIsOwner={viewerIsOwner}
            />
            <div>
              <h3 className='attachments-heading'>Attachments</h3>
              {
                attachmentsWithPdfFirst(files).map(file => (
                  <FileView
                    actor={actor}
                    context={context}
                    elementId={fileResponse.get('element-id')}
                    eventType='vault'
                    id={file.get('id')}
                    isViewMode
                    itemName={itemName}
                    itemResponse={itemResponse}
                    key={file.get('id')}
                    name={file.get('name')}
                    pdfPreviewEventName='click_pdf_preview'
                  />
                ))
              }
            </div>
          </div>
          <AudioPlayerBar />
          {!viewerIsOwner && <DeputyRecordingFinishLogger userConfig={userConfig} />}
        </Modals.CardModal>
      </AudioPlayerProvider>
    </div>
  )
}

LifeStoryItemModal.propTypes = {
  context: PropTypes.string,
  ethicalWill: PropTypes.instanceOf(Map),
  everplanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  exit: PropTypes.func,
  groupId: PropTypes.string,
  itemResponse: PropTypes.instanceOf(Map),
  ownerFirstName: PropTypes.string,
  userConfig: PropTypes.instanceOf(Map),
  viewerIsOwner: PropTypes.bool
}

export default LifeStoryItemModal
